// v2.19.24 — OrdonnanceCard : rendu structuré de la lecture d'ordonnance médicale
// par AYA Vision. Affiché dans le flux du chat sous la bulle agent quand le
// backend a renvoyé un ui_component de type "ordonnance_card".

import { useState } from "react";

const COLORS = {
  primary: "#DC2626",     // rouge médical
  primarySoft: "rgba(220,38,38,0.10)",
  primaryBorder: "rgba(220,38,38,0.35)",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.12)",
  amberBorder: "rgba(245,158,11,0.4)",
  emerald: "#10B981",
  emeraldSoft: "rgba(16,185,129,0.10)",
  emeraldBorder: "rgba(16,185,129,0.4)",
  slate: "#94A3B8",
};

function Chip({ children, accent = COLORS.slate }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: `${accent}1F`,
      color: accent,
      border: `1px solid ${accent}55`,
    }}>{children}</span>
  );
}

function MedicamentCard({ med, idx }) {
  return (
    <div data-testid={`ordo-med-${idx}`} style={{
      padding: "10px 12px",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 10,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#F1F5F9" }}>
        💊 {med.nom || "Médicament non identifié"}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {med.dosage && <Chip accent="#60A5FA">Dosage : {med.dosage}</Chip>}
        {med.posologie && <Chip accent="#A78BFA">Posologie : {med.posologie}</Chip>}
        {med.duree && <Chip accent="#34D399">Durée : {med.duree}</Chip>}
      </div>
    </div>
  );
}

function AlternativesTable({ alternatives }) {
  if (!Array.isArray(alternatives) || alternatives.length === 0) return null;
  return (
    <div data-testid="ordo-alternatives" style={{ marginTop: 12 }}>
      <h4 style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9", margin: "0 0 6px" }}>
        🌿 Alternatives génériques (CIV)
      </h4>
      <div style={{
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 10,
        overflow: "hidden",
        fontSize: 12,
      }}>
        {alternatives.map((alt, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 8,
            padding: "8px 10px",
            background: i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent",
            color: "#CBD5E1",
            alignItems: "center",
          }}>
            <span title="Original">{alt.medicament_original || "—"}</span>
            <span title="Générique" style={{ color: "#F1F5F9", fontWeight: 600 }}>
              → {alt.generique || "—"}
            </span>
            {alt.prix_approx_fcfa && (
              <Chip accent={COLORS.emerald}>{alt.prix_approx_fcfa} FCFA</Chip>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertesBlock({ alertes }) {
  if (!Array.isArray(alertes) || alertes.length === 0) return null;
  return (
    <div data-testid="ordo-alertes" style={{
      marginTop: 12,
      padding: "10px 12px",
      background: COLORS.amberSoft,
      border: `1px solid ${COLORS.amberBorder}`,
      borderRadius: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.amber, marginBottom: 4 }}>
        ⚠️ Alertes & vigilances
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, color: "#FCD34D", fontSize: 12 }}>
        {alertes.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
      </ul>
    </div>
  );
}

function PharmaciesBlock({ pharmacies }) {
  if (!Array.isArray(pharmacies) || pharmacies.length === 0) return null;
  return (
    <div data-testid="ordo-pharmacies" style={{ marginTop: 12 }}>
      <h4 style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9", margin: "0 0 6px" }}>
        💊 Pharmacies de garde proches
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {pharmacies.map((ph, i) => (
          <div key={ph.id || i} data-testid={`ordo-pharma-${i}`} style={{
            padding: "8px 10px",
            background: COLORS.emeraldSoft,
            border: `1px solid ${COLORS.emeraldBorder}`,
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <strong style={{ color: "#F1F5F9" }}>{ph.nom}</strong>
              <span style={{ color: "#94A3B8", fontSize: 11 }}>{ph.adresse}</span>
              {ph.heures && <Chip accent={COLORS.emerald}>🕐 {ph.heures}</Chip>}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {ph.telephone && (
                <a href={`tel:${ph.telephone}`}
                   data-testid={`ordo-pharma-call-${i}`}
                   style={{
                     padding: "5px 10px", borderRadius: 6, fontSize: 11,
                     background: "rgba(16,185,129,0.2)", color: "#34D399",
                     textDecoration: "none", border: "1px solid rgba(16,185,129,0.4)",
                     fontWeight: 600,
                   }}>📞 Appeler</a>
              )}
              {(ph.lat && ph.lng) && (
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${ph.lat},${ph.lng}`}
                   target="_blank" rel="noopener noreferrer"
                   data-testid={`ordo-pharma-go-${i}`}
                   style={{
                     padding: "5px 10px", borderRadius: 6, fontSize: 11,
                     background: "rgba(59,130,246,0.2)", color: "#93C5FD",
                     textDecoration: "none", border: "1px solid rgba(59,130,246,0.4)",
                     fontWeight: 600,
                   }}>🗺️ Y aller</a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OrdonnanceCard({ data }) {
  const [saving, setSaving] = useState(false);
  if (!data || typeof data !== "object") return null;

  // Cas d'échec lecture : afficher un message simple.
  if (data.lisible === false) {
    return (
      <div data-testid="ordo-card-unreadable" style={{
        marginTop: 8,
        padding: "12px 14px",
        background: COLORS.amberSoft,
        border: `1px solid ${COLORS.amberBorder}`,
        borderRadius: 10,
        fontSize: 13, color: "#FCD34D",
      }}>
        ⚠️ {data.raison || "Cette image ne semble pas être une ordonnance médicale lisible. Réessaie avec une photo plus nette."}
      </div>
    );
  }

  const confiance = typeof data.confiance === "number" ? data.confiance : null;
  const medicaments = Array.isArray(data.medicaments) ? data.medicaments : [];
  const disclaimer = data.disclaimer || "⚠️ Cette lecture est indicative. Toujours vérifier avec votre pharmacien.";

  return (
    <div data-testid="ordonnance-card" style={{
      marginTop: 10,
      padding: "14px 16px",
      background: COLORS.primarySoft,
      border: `1px solid ${COLORS.primaryBorder}`,
      borderRadius: 14,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.primary }}>
          📋 Lecture de votre ordonnance
        </h3>
        {confiance !== null && (
          <Chip accent={confiance >= 70 ? COLORS.emerald : COLORS.amber}>
            Confiance {confiance}%
          </Chip>
        )}
      </div>

      {medicaments.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {medicaments.map((m, i) => <MedicamentCard key={i} med={m} idx={i} />)}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: COLORS.slate, fontStyle: "italic" }}>
          Aucun médicament n'a pu être extrait. Réessaie avec une photo plus nette ou un meilleur cadrage.
        </div>
      )}

      <AlternativesTable alternatives={data.alternatives_generiques} />
      <AlertesBlock alertes={data.alertes} />
      <PharmaciesBlock pharmacies={data.pharmacies_proches} />

      <div style={{
        marginTop: 6,
        paddingTop: 8,
        borderTop: "1px dashed rgba(255,255,255,0.08)",
        fontSize: 11,
        color: COLORS.slate,
        fontStyle: "italic",
      }}>
        {disclaimer}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          data-testid="ordo-save-btn"
          onClick={() => { setSaving(true); setTimeout(() => setSaving(false), 1200); }}
          disabled={saving}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            background: "rgba(148,163,184,0.12)",
            color: "#CBD5E1",
            border: "1px solid rgba(148,163,184,0.3)",
            cursor: saving ? "default" : "pointer",
          }}
        >
          {saving ? "✓ Sauvegardée" : "💾 Sauvegarder cette ordonnance"}
        </button>
      </div>
    </div>
  );
}
